// ⓘ mit eigener Erklärungsblase — bewusst kein `title` (Browser-Tooltip
// erscheint verzögert und ist nicht anklickbar) und kein Fluent-Popover.
//
// Öffnet beim Überfahren UND beim Klick; ein Klick hält die Blase offen, bis
// man daneben klickt oder Escape drückt. Die Blase hängt per Portal am <body>
// und sitzt `fixed` — in der Matrix läge sie sonst im scrollenden
// Tabellen-Container und würde an dessen Rand abgeschnitten.

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { BRAND } from "../risikoanalyseStep/tokens";
import { alleDokumente, alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

const knopfStil: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 15,
  height: 15,
  marginLeft: 4,
  padding: 0,
  border: `1px solid ${BRAND.border}`,
  borderRadius: 999,
  background: BRAND.white,
  color: BRAND.purple,
  fontFamily: "Georgia, serif",
  fontStyle: "italic",
  fontWeight: 700,
  fontSize: 10,
  lineHeight: 1,
  cursor: "pointer",
  verticalAlign: "middle",
  textTransform: "none",
  letterSpacing: 0,
};

const BREITE = 260;

export function InfoTipp({ text, label = "Erklärung" }: { text: string; label?: string }) {
  const knopf = useRef<HTMLButtonElement | null>(null);
  const [hover, setHover] = useState(false);
  const [fest, setFest] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const offen = hover || fest;

  useEffect(() => {
    if (!offen || !knopf.current) return;
    const r = knopf.current.getBoundingClientRect();
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - BREITE / 2),
      aktivesFenster().innerWidth - BREITE - 8,
    );
    setPos({ top: r.bottom + 6, left });
  }, [offen]);

  useEffect(() => {
    if (!fest) return;
    const zu = (e: MouseEvent) => {
      if (knopf.current && !knopf.current.contains(e.target as Node)) setFest(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setFest(false);
    const scroll = () => setFest(false);
    alleDokumente.addEventListener("mousedown", zu);
    alleDokumente.addEventListener("keydown", esc);
    alleFenster.addEventListener("scroll", scroll, true);
    return () => {
      alleDokumente.removeEventListener("mousedown", zu);
      alleDokumente.removeEventListener("keydown", esc);
      alleFenster.removeEventListener("scroll", scroll, true);
    };
  }, [fest]);

  return (
    <>
      <button
        ref={knopf}
        type="button"
        style={knopfStil}
        aria-label={label}
        aria-expanded={offen}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={(e) => {
          // Die Zelle darunter (Drill-Down) soll vom ⓘ nichts mitbekommen.
          e.stopPropagation();
          setFest((v) => !v);
        }}
      >
        i
      </button>
      {offen &&
        pos &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: BREITE,
              zIndex: 10000,
              padding: "8px 10px",
              background: BRAND.white,
              color: BRAND.ink,
              border: `1px solid ${BRAND.border}`,
              borderLeft: `3px solid ${BRAND.purple}`,
              borderRadius: 8,
              boxShadow: "0 6px 18px rgba(45,20,43,0.14)",
              fontFamily: BRAND.fontPrimary,
              fontSize: "0.78rem",
              fontWeight: 500,
              lineHeight: 1.45,
              textAlign: "left",
              textTransform: "none",
              letterSpacing: 0,
              whiteSpace: "normal",
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
